import { filter } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import {
  ProviderToken,
  Signal,
  computed,
  effect,
  inject,
  linkedSignal,
} from '@angular/core';
import { TablePagination, TableSort } from '@merchant-webapp/components';
import {
  DeepSignal,
  EmptyFeatureResult,
  SignalStoreFeature,
  WritableStateSource,
  patchState,
  signalStoreFeature,
  withComputed,
  withHooks,
  withMethods,
  withState,
  withLinkedState,
} from '@ngrx/signals';
import {
  EntityId,
  EntityState,
  NamedEntityState,
  addEntity,
  removeEntity,
  setAllEntities,
  updateEntity,
} from '@ngrx/signals/entities';
import { cleanHttpParamsInputObject } from '../../shared';
import {
  ErCallState,
  ErNamedCallStateSlice,
  getCallStateKeys,
  setBlockingLoading,
  setError,
  setLoaded,
  setLoading,
} from './with-er-call-state';
import { DashboardDataService } from '../../app/components/dashboard/services/dashboard.store';

export type Filter = Record<string, unknown>;
export type FilterInput = Record<string, unknown>;
export type Entity = { id: EntityId };

// export type Filter = {
//   pagination: TablePagination;
//   sort: TableSort;
//   // TODO: This type is hardcoded.
//   filters: StationFilterInputData;
// };

export type ApiResponse<T extends Entity> = {
  pageable: TablePagination;
  data: T[];
  filters: FilterInput;
};

export type DataServiceInitialConfig<F extends Filter, FD extends Filter> = {
  filter: F;
  sort: TableSort;

  filterData?: FD;

  // TODO: This will still be neded when initial filter data is not sync
  // because we made filters on dataService a linkedSignal, duplicate calls are no longer an issue with sync data
  // The case where initial filter data is not available when the page first loads, we still need this function to prevent the
  // api call with empty data.
  mandatoryFilterCheck?: (filter: F) => boolean;
  parentFilterName?: string;
  transformParentFilters?: (
    filter: Record<string, unknown>,
  ) => Record<string, unknown>;
};

// TODO: Separate types list vs edit (Stations vs PlusStationRequest)
export interface DataService<
  E extends Entity,
  // TODO: Can we make type inference work from here?
  //   F extends Filter,
  //   FD extends Filter,
> {
  load(httpParams: HttpParams): Promise<ApiResponse<E>>;

  loadById(id: EntityId): Promise<E>;

  create(entity: E): Promise<E>;

  update(entity: E): Promise<E>;

  updateAll(entity: E[]): Promise<E[]>;

  delete(entity: E): Promise<void>;
}

export function capitalize(str: string): string {
  return str ? str[0].toUpperCase() + str.substring(1) : str;
}

export function getDataServiceKeys(options: { collection?: string }) {
  const sortingKey = options.collection
    ? `${options.collection}Sorting`
    : 'sorting';
  const paginationKey = options.collection
    ? `${options.collection}Pagination`
    : 'pagination';
  const filterInputsDataKey = options.collection
    ? `${options.collection}FilterInputsData`
    : 'filterInputsData';
  const filterKey = options.collection
    ? `${options.collection}Filter`
    : 'filter';
  const selectedIdsKey = options.collection
    ? `selected${capitalize(options.collection)}Ids`
    : 'selectedIds';
  const selectedEntitiesKey = options.collection
    ? `selected${capitalize(options.collection)}Entities`
    : 'selectedEntities';

  const updateSortingKey = options.collection
    ? `update${capitalize(options.collection)}Sorting`
    : 'updateSorting';
  const updatePageSizeKey = options.collection
    ? `update${capitalize(options.collection)}PageSize`
    : 'updatePageSize';

  const updatePageIndexKey = options.collection
    ? `update${capitalize(options.collection)}PageIndex`
    : 'updatePageIndex';

  const updateFilterKey = options.collection
    ? `update${capitalize(options.collection)}Filter`
    : 'updateFilter';
  const partialUpdateFilterKey = options.collection
    ? `partialUpdate${capitalize(options.collection)}Filter`
    : 'partialUpdateFilter';
  const updateSelectedKey = options.collection
    ? `updateSelected${capitalize(options.collection)}Entities`
    : 'updateSelected';
  const loadKey = options.collection
    ? `load${capitalize(options.collection)}Entities`
    : 'load';

  const currentKey = options.collection
    ? `current${capitalize(options.collection)}`
    : 'current';
  const loadByIdKey = options.collection
    ? `load${capitalize(options.collection)}ById`
    : 'loadById';
  const setCurrentKey = options.collection
    ? `setCurrent${capitalize(options.collection)}`
    : 'setCurrent';
  const createKey = options.collection
    ? `create${capitalize(options.collection)}`
    : 'create';
  const updateKey = options.collection
    ? `update${capitalize(options.collection)}`
    : 'update';
  const updateAllKey = options.collection
    ? `updateAll${capitalize(options.collection)}`
    : 'updateAll';
  const deleteKey = options.collection
    ? `delete${capitalize(options.collection)}`
    : 'delete';

  // TODO: Take these from @ngrx/signals/entities, when they are exported
  const entitiesKey = options.collection
    ? `${options.collection}Entities`
    : 'entities';
  const entityMapKey = options.collection
    ? `${options.collection}EntityMap`
    : 'entityMap';
  const idsKey = options.collection ? `${options.collection}Ids` : 'ids';

  return {
    filterInputsDataKey,
    paginationKey,
    sortingKey,

    updatePageIndexKey,
    updatePageSizeKey,
    updateSortingKey,

    filterKey,
    selectedIdsKey,
    selectedEntitiesKey,
    updateFilterKey,
    partialUpdateFilterKey,
    updateSelectedKey,
    loadKey,
    entitiesKey,
    entityMapKey,
    idsKey,

    currentKey,
    loadByIdKey,
    setCurrentKey,
    createKey,
    updateKey,
    updateAllKey,
    deleteKey,
  };
}

export type NamedDataServiceState<
  E extends Entity,
  F extends Filter,
  FD extends Filter,
  Collection extends string,
> = {
  [K in Collection as `${K}Filter`]: F;
} & {
  [K in Collection as `selected${Capitalize<K>}Ids`]: Record<EntityId, boolean>;
} & {
  [K in Collection as `current${Capitalize<K>}`]: E;
} & {
  [K in Collection as `${K}FilterInputsData`]: FD;
} & {
  [K in Collection as `${K}Pagination`]: TablePagination;
} & {
  [K in Collection as `${K}Sorting`]: TableSort;
};

export type DataServiceState<
  E extends Entity,
  F extends Filter,
  FD extends Filter,
> = {
  filter: F;
  selectedIds: Record<EntityId, boolean>;
  current: E;
  filterInputsData: FD;
  pagination: TablePagination;
  sorting: TableSort;
};

export type DataServiceComputed<E extends Entity> = {
  selectedEntities: Signal<E[]>;
};

export type NamedDataServiceComputed<
  E extends Entity,
  Collection extends string,
> = {
  [K in Collection as `selected${Capitalize<K>}Entities`]: Signal<E[]>;
};

export type NamedDataServiceMethods<
  E extends Entity,
  F extends Filter,
  Collection extends string,
> = {
  [K in Collection as `update${Capitalize<K>}Sorting`]: (
    sort: TableSort,
  ) => void;
} & {
  [K in Collection as `update${Capitalize<K>}PageSize`]: (size: number) => void;
} & {
  [K in Collection as `update${Capitalize<K>}PageIndex`]: (
    index: number,
  ) => void;
} & {
  [K in Collection as `update${Capitalize<K>}Filter`]: (filter: F) => void;
} & {
  [K in Collection as `partialUpdate${Capitalize<K>}Filter`]: (
    filter: Partial<F>,
  ) => void;
} & {
  [K in Collection as `updateSelected${Capitalize<K>}Entities`]: (
    id: EntityId,
    selected: boolean,
  ) => void;
} & {
  [K in Collection as `load${Capitalize<K>}Entities`]: () => Promise<void>;
} & {
  [K in Collection as `setCurrent${Capitalize<K>}`]: (entity: E) => void;
} & {
  [K in Collection as `load${Capitalize<K>}ById`]: (
    id: EntityId,
  ) => Promise<void>;
} & {
  [K in Collection as `create${Capitalize<K>}`]: (entity: E) => Promise<void>;
} & {
  [K in Collection as `update${Capitalize<K>}`]: (entity: E) => Promise<void>;
} & {
  [K in Collection as `updateAll${Capitalize<K>}`]: (
    entity: E[],
  ) => Promise<void>;
} & {
  [K in Collection as `delete${Capitalize<K>}`]: (entity: E) => Promise<void>;
};

export type DataServiceMethods<E extends Entity, F extends Filter> = {
  updatePageSize: (size: number) => void;
  updateSorting: (sort: TableSort) => void;
  updatePageIndex: (index: number) => void;

  updateFilter: (filter: F) => void;
  partialUpdateFilter: (filter: Partial<F>) => void;
  updateSelected: (id: EntityId, selected: boolean) => void;
  load: () => Promise<void>;

  setCurrent(entity: E): void;
  loadById(id: EntityId): Promise<void>;
  create(entity: E): Promise<void>;
  update(entity: E): Promise<void>;
  updateAll(entities: E[]): Promise<void>;
  delete(entity: E): Promise<void>;
};

export function withMerchantDataService<
  E extends Entity,
  F extends Filter,
  FD extends Filter,
  Collection extends string,
>(options: {
  dataServiceType: ProviderToken<DataService<E>>;
  initialConfig: DataServiceInitialConfig<F, FD>;
  collection: Collection;
}): SignalStoreFeature<
  EmptyFeatureResult & {
    state: ErNamedCallStateSlice<Collection> & NamedEntityState<E, Collection>;
  },
  {
    state: NamedDataServiceState<E, F, FD, Collection>;
    props: NamedDataServiceComputed<E, Collection>;
    methods: NamedDataServiceMethods<E, F, Collection>;
  }
>;
export function withMerchantDataService<
  E extends Entity,
  F extends Filter,
  FD extends Filter,
>(options: {
  dataServiceType: ProviderToken<DataService<E>>;
  initialConfig: DataServiceInitialConfig<F, FD>;
}): SignalStoreFeature<
  EmptyFeatureResult & { state: { callState: ErCallState } & EntityState<E> },
  {
    state: DataServiceState<E, F, FD>;
    props: DataServiceComputed<E>;
    methods: DataServiceMethods<E, F>;
  }
>;

export function withMerchantDataService<
  E extends Entity,
  F extends Filter,
  FD extends Filter,
  Collection extends string,
>(options: {
  dataServiceType: ProviderToken<DataService<E>>;
  initialConfig: DataServiceInitialConfig<F, FD>;
  collection?: Collection;
}): /* eslint-disable @typescript-eslint/no-explicit-any */
SignalStoreFeature<any, any> {
  const { dataServiceType, initialConfig, collection: prefix } = options;
  const {
    filterInputsDataKey,
    paginationKey,
    sortingKey,

    updatePageIndexKey,
    updatePageSizeKey,
    updateSortingKey,

    entitiesKey,
    filterKey,
    loadKey,
    selectedEntitiesKey,
    selectedIdsKey,
    updateFilterKey,
    partialUpdateFilterKey,
    updateSelectedKey,

    currentKey,
    createKey,
    updateKey,
    updateAllKey,
    deleteKey,
    loadByIdKey,
    setCurrentKey,
  } = getDataServiceKeys(options);

  const { callStateKey } = getCallStateKeys({ collection: prefix });

  return signalStoreFeature(
    withState(() => ({
      [selectedIdsKey]: {} as Record<EntityId, boolean>,
      [currentKey]: undefined as E | undefined,
      [filterInputsDataKey]: {} as FD,
      [paginationKey]: {
        total_records: 0,
        page_index: 0,
        page_size: 10,
      } as TablePagination,
      [sortingKey]: initialConfig.sort ?? ({} as TableSort),
    })),
    withComputed((store: Record<string, unknown>) => {
      const entities = store[entitiesKey] as Signal<E[]>;
      const selectedIds = store[selectedIdsKey] as Signal<
        Record<EntityId, boolean>
      >;

      return {
        [selectedEntitiesKey]: computed(() =>
          entities().filter((e) => selectedIds()[e.id]),
        ),
      };
    }),
    withLinkedState((store: Record<string, unknown>) => ({
      [filterKey]: initialConfig.parentFilterName
        ? linkedSignal({
            source: computed(() => {
              let fn = (x: any) => x;
              if (initialConfig.transformParentFilters) {
                fn = initialConfig.transformParentFilters;
              }
              return fn(
                (store[initialConfig.parentFilterName!] as Signal<object>)(),
              );
            }),
            computation: (source, previous) => ({
              ...initialConfig.filter,
              ...(previous?.value ?? {}),
              ...source,
            }),
          })
        : () => ({ ...initialConfig.filter }),
    })),
    withMethods(
      (store: Record<string, unknown> & WritableStateSource<object>) => {
        const dataService = inject(dataServiceType);

        const updatePageIndex = (index: number): void => {
          patchState(store, (state: Record<string, unknown>) => ({
            [paginationKey]: {
              ...(state[paginationKey] as TablePagination),
              page_index: index,
            },
          }));
        };

        return {
          [updatePageSizeKey]: (size: number): void => {
            patchState(store, {
              [paginationKey]: { page_size: size, page_index: 0 },
            });
          },
          [updatePageIndexKey]: updatePageIndex,
          [partialUpdateFilterKey]: (filter: Partial<F>): void => {
            //   setTimeout(() => {
            updatePageIndex(0);
            const oldFilters = (store[filterKey] as Signal<F>)();
            /*if (
              // TODO: Simple check to avoid duplicate calls on 
            Object.entries(filter).reduce((acc, [key, val]) => {
              if (oldFilters[key] !== val) {
                acc = false;
              }
              return acc;
            }, true)
          ) {
            return;
          } */
            patchState(store, (state) => ({
              [filterKey]: { ...oldFilters, ...filter },
            }));
            // }, 1000);
          },
          [updateFilterKey]: (filter: Partial<F>): void => {
            updatePageIndex(0);
            patchState(store, { [filterKey]: filter });
          },
          [updateSortingKey]: (sort: TableSort): void => {
            updatePageIndex(0);
            if (!sort.sorting_direction) {
              patchState(store, { [sortingKey]: initialConfig.sort });
              return;
            }
            patchState(store, { [sortingKey]: sort });
          },
          [updateSelectedKey]: (id: EntityId, selected: boolean): void => {
            patchState(store, (state: Record<string, unknown>) => ({
              [selectedIdsKey]: {
                ...(state[selectedIdsKey] as Record<EntityId, boolean>),
                [id]: selected,
              },
            }));
          },
          [loadKey]: async (): Promise<void> => {
            const filter = store[filterKey] as Signal<F>;
            const pagination = store[
              paginationKey
            ] as DeepSignal<TablePagination>;
            const sorting = store[sortingKey] as Signal<TableSort>;
            (() =>
              store[callStateKey] &&
              patchState(store, setLoading(prefix), { weird: true }))();
            /* 
          if (initialConfig.mandatoryFilterCheck && !initialConfig.mandatoryFilterCheck(filter())) {
            return;
          } */
            const params = new HttpParams({
              fromObject: cleanHttpParamsInputObject({
                ...filter(),
                page_size: pagination.page_size(),
                page_index: pagination.page_index(),
                ...sorting(),
              }),
            });

            try {
              const result = await dataService.load(params);
              // TODO: This is not nice, either abstract the data mapping, or if possible align with bff to have a common interface for all paginated "tables"
              if (dataService instanceof DashboardDataService) {
                const { content, ...rest } = result as any;
                patchState(
                  store,
                  prefix
                    ? setAllEntities(content, { collection: prefix })
                    : setAllEntities(content),
                  () => ({
                    [paginationKey]: {
                      page_size: rest.size,
                      page_index: rest.number,
                      remaining_pages: 0,
                      total_pages: rest.totalPages,
                      total_records: rest.totalElements,
                    },
                  }),
                );
              } else {
                patchState(
                  store,
                  prefix
                    ? setAllEntities(result.data, { collection: prefix })
                    : setAllEntities(result.data),
                  () => ({
                    [filterInputsDataKey]: result.filters,
                  }),
                  () => ({
                    [paginationKey]: result.pageable,
                  }),
                );
              }
              (() =>
                store[callStateKey] && patchState(store, setLoaded(prefix)))();
            } catch (e) {
              (() =>
                store[callStateKey] &&
                patchState(store, setError(e, prefix)))();
              throw e;
            }
          },
          [loadByIdKey]: async (id: EntityId): Promise<void> => {
            (() =>
              store[callStateKey] &&
              patchState(store, setBlockingLoading(prefix)))();
            patchState(store, { [currentKey]: {} });

            try {
              const current = await dataService.loadById(id);
              (() =>
                store[callStateKey] && patchState(store, setLoaded(prefix)))();
              patchState(store, { [currentKey]: current });
            } catch (e) {
              (() =>
                store[callStateKey] &&
                patchState(store, setError(e, prefix)))();
              throw e;
            }
          },
          [setCurrentKey]: (current: E): void => {
            patchState(store, { [currentKey]: current });
          },
          [createKey]: async (entity: E): Promise<void> => {
            patchState(store, { [currentKey]: entity });
            (() =>
              store[callStateKey] && patchState(store, setLoading(prefix)))();

            try {
              const created = await dataService.create(entity);
              patchState(store, { [currentKey]: created });
              patchState(
                store,
                prefix
                  ? addEntity(created, { collection: prefix })
                  : addEntity(created),
              );
              (() =>
                store[callStateKey] && patchState(store, setLoaded(prefix)))();
            } catch (e) {
              (() =>
                store[callStateKey] &&
                patchState(store, setError(e, prefix)))();
              throw e;
            }
          },
          [updateKey]: async (entity: E): Promise<void> => {
            patchState(store, { [currentKey]: entity });
            (() =>
              store[callStateKey] &&
              patchState(store, setBlockingLoading(prefix)))();

            try {
              const updated = await dataService.update(entity);
              patchState(store, { [currentKey]: updated });

              const updateArg = {
                id: updated.id,
                changes: updated,
              };

              const updater = (collection: string) =>
                updateEntity(updateArg, { collection });

              patchState(
                store,
                prefix ? updater(prefix) : updateEntity(updateArg),
              );
              (() =>
                store[callStateKey] && patchState(store, setLoaded(prefix)))();
            } catch (e) {
              (() =>
                store[callStateKey] &&
                patchState(store, setError(e, prefix)))();
              throw e;
            }
          },
          [updateAllKey]: async (entities: E[]): Promise<void> => {
            (() =>
              store[callStateKey] && patchState(store, setLoading(prefix)))();

            try {
              const result = await dataService.updateAll(entities);
              patchState(
                store,
                prefix
                  ? setAllEntities(result, { collection: prefix })
                  : setAllEntities(result),
              );
              (() =>
                store[callStateKey] && patchState(store, setLoaded(prefix)))();
            } catch (e) {
              (() =>
                store[callStateKey] &&
                patchState(store, setError(e, prefix)))();
              throw e;
            }
          },
          [deleteKey]: async (entity: E): Promise<void> => {
            patchState(store, { [currentKey]: entity });
            (() =>
              store[callStateKey] && patchState(store, setLoading(prefix)))();

            try {
              await dataService.delete(entity);
              patchState(store, { [currentKey]: undefined });
              patchState(
                store,
                prefix
                  ? removeEntity(entity.id, { collection: prefix })
                  : removeEntity(entity.id),
              );
              (() =>
                store[callStateKey] && patchState(store, setLoaded(prefix)))();
            } catch (e) {
              (() =>
                store[callStateKey] &&
                patchState(store, setError(e, prefix)))();
              throw e;
            }
          },
        };
      },
    ),
    withHooks({
      onInit: (store: Record<string, unknown>) => {
        // TODO: This effect will need to be customized more, or maybe leave it intentionally ouside the store feature and write directly on each particular store
        // This is the place where we should control any potential duplicate calls which I just introduced on dashboard
        // I'm still not sure what the best approach is here. Maybe separate the dashboard store into 2 and make them communicate? Might not solve the issue at all
        // Maybe we could pass a function param to withMerchantDataService which checks if filters have minimum required values (in this case for dashboard timePeriod). I just don't want to add too many abstractions and keep the interface as simple as possible
        effect(() => {
          const load = store[loadKey] as () => void;
          load();
        });
      },
    }),
  );
}
